import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
// import { Mail, Lock, User, LogIn } from 'lucide-react'; // removed unused imports
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n'; // Importando i18n para mudar o idioma
import axios from 'axios'; // Importando axios
import EmailVerificationModal from './EmailVerificationModal/EmailVerificationModal'; // Novo import

const AuthPage: React.FC = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState(''); // Novo estado para confirmação de e-mail
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false); // Novo estado para o modal

  // Determina a URL de redirecionamento. Usando o domínio de produção.
  // NOTA: Em produção, o REACT_APP_PUBLIC_URL deve ser 'https://mibuscador.scalizai.com'
  const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'https://mibuscador.scalizai.com';

  const WEBHOOK_REGISTER_URL = 'https://webhook.solvion.com.br/webhook/user-register';

  const availableLanguages = [
    { code: 'pt-BR', name: t('languages.pt-BR') },
    { code: 'es', name: t('languages.es') },
    { code: 'en', name: t('languages.en') },
    { code: 'fr', name: t('languages.fr') },
  ];

  const currentLanguage = i18n.language;

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  const handlePasswordRecovery = async () => {
    if (!email) {
      toast.error(t('Por favor, insira seu e-mail para recuperação.'));
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        // Redireciona para a rota de redefinição de senha no domínio correto
        redirectTo: `${publicUrl}/update-password`,
      });

      if (error) throw error;

      toast.success(t('Instruções de recuperação enviadas! Verifique seu e-mail.'));
      setIsRecoveryMode(false);
    } catch (error) {
      let errorMessage = error instanceof Error ? error.message : t('Erro ao solicitar recuperação de senha.');

      if (errorMessage.includes('No user found')) {
        errorMessage = t('Nenhum usuário encontrado com este e-mail.');
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isRecoveryMode) {
      handlePasswordRecovery();
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success(t('Login realizado com sucesso!'));
      } else {
        // Validação de Confirmação de E-mail
        if (email !== confirmEmail) {
          toast.error(t('Os e-mails não coincidem.'));
          setLoading(false);
          return;
        }

        // Validação de Confirmação de Senha
        if (password !== confirmPassword) {
          toast.error(t('As senhas não coincidem.'));
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName }
          }
        });
        if (error) throw error;

        // Chamar o Webhook de Registro (fire-and-forget)
        try {
          await axios.post(WEBHOOK_REGISTER_URL, { email: email });
        } catch {
          // Falha silenciosa: não bloqueia o fluxo de registro
        }

        // Exibir modal em vez de toast
        setIsVerificationModalOpen(true);

      }
    } catch (error) {
      let errorMessage = 'Erro desconhecido.';

      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Mapeamento de mensagens comuns para o usuário
      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = t('Credenciais inválidas. Verifique seu e-mail e senha.');
      } else if (errorMessage.includes('Email not confirmed')) {
        errorMessage = t('E-mail não confirmado. Verifique sua caixa de entrada.');
      } else if (errorMessage.includes('User already registered')) {
        errorMessage = t('Este e-mail já está cadastrado.');
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setIsRecoveryMode(false);
    setFullName('');
    setConfirmPassword('');
    setConfirmEmail(''); // Limpa a confirmação de e-mail
  };

  const toggleRecoveryMode = () => {
    setIsRecoveryMode(!isRecoveryMode);
    setIsLogin(true); // Recuperação de senha sempre começa na tela de login
  };

  const titleText = isRecoveryMode ? t('Recuperação de Senha') : (isLogin ? t('auth.welcome_back') : t('auth.create_account'));

  return (
    <>
      <motion.div
        className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className="w-full max-w-md space-y-8"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Language Selector */}
          <div className="flex justify-end">
            <div className="relative max-w-[150px]">
              <select
                className="w-full appearance-none rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 pl-3 pr-8 text-sm text-[#181411] dark:text-white focus:border-primary focus:ring-primary"
                value={currentLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
              >
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
              <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-lg">language</span>
            </div>
          </div>

          {/* Header Section */}
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex items-center gap-3 text-[#181411] dark:text-white">
              <img
                src="/logoscaliz.png"
                alt="Scaliz Logo"
                className="size-8"
              />
              <h1 className="text-3xl font-bold leading-tight tracking-[-0.015em]">{t('app.title')}</h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {titleText}
            </p>
          </div>

          {/* Form Card */}
          <div className="flex flex-col gap-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 sm:p-8">

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">

              {/* Email (Always visible) */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="email">{t('auth.email_label')}</label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    placeholder="seuemail@exemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                    required
                  />
                </div>
              </div>

              {/* Confirm Email (Register only) */}
              {!isLogin && !isRecoveryMode && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="confirmEmail">{t('auth.confirm_email_label')}</label>
                  <div className="relative">
                    <input
                      id="confirmEmail"
                      type="email"
                      placeholder="confirme seu email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Password (Login/Register only) */}
              {!isRecoveryMode && (
                <>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="password">{t('auth.password_label')}</label>
                    <div className="relative">
                      <input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                        required
                      />
                    </div>
                  </div>

                  {/* Confirm Password (Register only) */}
                  {!isLogin && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="confirmPassword">{t('auth.confirm_password_label')}</label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                          required={!isLogin}
                        />
                      </div>
                    </div>
                  )}

                  {/* Full Name (Register only) */}
                  {!isLogin && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="fullName">{t('auth.full_name_label')}</label>
                      <div className="relative">
                        <input
                          id="fullName"
                          type="text"
                          placeholder="Seu nome completo"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                          required={!isLogin}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Submit Button (Gradient) */}
              <motion.button
                type="submit"
                className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-to-r from-[#f98006] to-[#ffb800] text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin mr-2">sync</span>
                    {t('auth.loading')}
                  </>
                ) : (
                  isRecoveryMode ? t('Enviar Link de Recuperação') : (isLogin ? t('auth.login_button') : t('auth.register_button'))
                )}
              </motion.button>
            </form>

            {/* Footer Links */}
            <div className="flex justify-between text-sm pt-2">
              {isRecoveryMode ? (
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={toggleRecoveryMode}
                >
                  {t('Voltar para Login')}
                </button>
              ) : (
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={toggleRecoveryMode}
                >
                  {t('auth.forgot_password')}
                </button>
              )}

              {!isRecoveryMode && (
                <button
                  type="button"
                  onClick={toggleAuthMode}
                  className="font-medium text-primary hover:underline"
                >
                  {isLogin ? t('auth.register_link') : t('auth.login_link')}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Modal de Verificação de E-mail */}
      <EmailVerificationModal
        isOpen={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        email={email}
      />
    </>
  );
};

export default AuthPage;